import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';

/**
 * Hook that manages amphitheater seat hold countdown timers.
 * - Tracks per-item timers (each seat has its own countdown based on its expiresAt)
 * - Auto-releases holds and removes cart items when their individual timer hits 0
 * - Returns a Map of itemId -> secondsLeft
 */
export function useHoldTimer() {
  // Map of itemId -> secondsLeft
  const [timers, setTimers] = useState({});
  const intervalRef = useRef(null);

  // Calculate seconds remaining for a given expiresAt ISO string
  const getSecondsRemaining = useCallback((expiresAt) => {
    if (!expiresAt) return null;
    const diff = Math.floor((new Date(expiresAt) - new Date()) / 1000);
    return diff > 0 ? diff : 0;
  }, []);

  // Release holds and remove expired amphitheater items from cart
  const releaseExpiredItems = useCallback(() => {
    try {
      const cartRaw = localStorage.getItem('cart');
      if (!cartRaw) return;
      const cart = JSON.parse(cartRaw);
      if (!cart.items) return;

      const now = new Date();
      const expiredItems = cart.items.filter(
        item => item.type === 'amphitheater' && item.expiresAt && new Date(item.expiresAt) <= now
      );
      const validItems = cart.items.filter(
        item => !(item.type === 'amphitheater' && item.expiresAt && new Date(item.expiresAt) <= now)
      );

      if (expiredItems.length > 0) {
        // Release backend holds
        expiredItems.forEach(item => {
          if (item.holdIds) {
            item.holdIds.forEach(holdId => {
              api.releaseSeatHold(holdId).catch(() => {});
            });
          }
        });

        // Update cart
        const newTotal = validItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
        localStorage.setItem('cart', JSON.stringify({ items: validItems, total: newTotal }));
        window.dispatchEvent(new CustomEvent('cartUpdated', {
          detail: { items: validItems, total: newTotal }
        }));
        window.dispatchEvent(new CustomEvent('holdExpired'));
      }
    } catch (e) {
      console.error('Error releasing expired items:', e);
    }
  }, []);

  // Tick: update per-item timers and auto-release if any expired
  const tick = useCallback(() => {
    try {
      const cartRaw = localStorage.getItem('cart');
      if (!cartRaw) { setTimers({}); return; }
      const cart = JSON.parse(cartRaw);
      if (!cart.items) { setTimers({}); return; }

      const now = new Date();
      let hasExpired = false;
      const newTimers = {};

      cart.items.forEach(item => {
        if (item.type === 'amphitheater' && item.expiresAt) {
          const remaining = Math.floor((new Date(item.expiresAt) - now) / 1000);
          if (remaining <= 0) {
            hasExpired = true;
            newTimers[item.id] = 0;
          } else {
            newTimers[item.id] = remaining;
          }
        }
      });

      if (hasExpired) {
        releaseExpiredItems();
      }

      setTimers(newTimers);
    } catch (e) {
      setTimers({});
    }
  }, [releaseExpiredItems]);

  useEffect(() => {
    // Clean up expired items on mount
    releaseExpiredItems();
    // Initial tick
    tick();
    // Tick every second
    intervalRef.current = setInterval(tick, 1000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [tick, releaseExpiredItems]);

  // Listen for cart updates to re-check
  useEffect(() => {
    const handler = () => tick();
    window.addEventListener('cartUpdated', handler);
    return () => window.removeEventListener('cartUpdated', handler);
  }, [tick]);

  // Format seconds as M:SS
  const formatTime = useCallback((secs) => {
    if (secs === null || secs === undefined) return '';
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }, []);

  // Get seconds left for a specific item
  const getItemSeconds = useCallback((itemId) => {
    return timers[itemId] !== undefined ? timers[itemId] : null;
  }, [timers]);

  // Check if any amphitheater items have active timers
  const hasActiveTimers = Object.values(timers).some(s => s > 0);

  return { timers, getItemSeconds, formatTime, releaseExpiredItems, getSecondsRemaining, hasActiveTimers };
}
